import { useTheme } from "@emotion/react";
import { Button, Slider, TextField } from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import { AudioSchedulerContext } from "../audio/audioSchedulerContext";
import { useMuted } from "../audio/mutedContext";
import ConfirmDialog from "../components/ConfirmDialog";
import MuteButton from "../components/MuteButton";
import { PhaseRingSegments } from "../components/clockLayout/PhaseRingSegments";
import { useConfig } from "../config/configContext";
import useStopwatch from "../hooks/useStopwatch";
import { useUpdateTitle } from "../hooks/useUpdateTitle";
import { getThemeColor } from "../utils/color";
import { randomElement, randomInt } from "../utils/random";
import { convertSecondsToDurationString } from "../utils/time";
import { PlannedPhase, getUpcomingAndPastPhases } from "./Room";

const DEFAULT_PAUSE_IMAGES = [
    "/img/Schreibtisch.png"
];

export const Timer = () => {
    const { individualTimer: conf } = useConfig();
    const [audioScheduler, playingAudio] = useContext(AudioSchedulerContext);
    const theme = useTheme() as any;

    const theDiceAreThrown = useMemo(() => {
        const audioFlavorIndex = randomInt(conf.audioFlavors.length || 0);
        const pauseImage = randomElement(conf.pauseImages || DEFAULT_PAUSE_IMAGES);
        console.debug("theDiceAreThrown 🎲", { audioFlavorIndex, pauseImage })
        return {
            audioFlavorIndex,
            pauseImage
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const allAudios = useMemo(() => {
        return conf.phases
            .map(phase => phase.audio?.[theDiceAreThrown.audioFlavorIndex])
            .filter(audio => audio) as string[];
    }, [conf.phases, theDiceAreThrown.audioFlavorIndex]);
    
    //preload all audios
    useEffect(() => {
        audioScheduler.preload(allAudios);
    }, [audioScheduler, allAudios]);

    const roomColor = (conf.color || "twa-green") as any;
    const roomColorCode = getThemeColor(theme, conf.color || "twa-green");

    const [focusPhaseLength, setFocusPhaseLength] = useState(conf.defaultFocusDuration);
    const [intention, setIntention] = useState("");
    const [running, setRunning] = useState(false);

    const isPlayingAudio = playingAudio !== null;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [muted, setMuted] = useMuted();

    const [showConfirmStopModal, setShowConfirmStopModal] = useState(false);

    const { phases, totalSessionDuration } = useMemo(() => {
        //iterate over conf.phases, adjust focus phase length, and and adjust all endTime/startTime values after that
        const phases: PlannedPhase[] = [];
        let totalSessionDuration = 0;
        let lastEndTime = 0;
        conf.phases.forEach((phase, index) => {
            const copy = { ...phase };
            if (phase.type === "focus") {
                copy.duration = focusPhaseLength;
            }
            copy.startTime = lastEndTime;
            copy.endTime = copy.startTime + copy.duration;
            lastEndTime = copy.endTime;
            if (!copy.hiddenOnClock) {
                totalSessionDuration += copy.duration;
            }
            phases.push(copy);
        });
        return { phases, totalSessionDuration };
    }, [conf.phases, focusPhaseLength]);

    const stopwatch = useStopwatch();

    let upcomingPhases = useMemo(() => {
        return getUpcomingAndPastPhases(phases, stopwatch.elapsedSeconds, true)[0];
    }, [phases, stopwatch.elapsedSeconds]);

    const stopRequested = () => {
        setShowConfirmStopModal(true);
    }
    const stopConfirmed = () => {
        audioScheduler.stopAllIncludingSilence();
        setRunning(false);
        setIntention("");
        stopwatch.reset();
        setShowConfirmStopModal(false);
    }
    const start = () => {
        audioScheduler.playSilence();
        stopwatch.start();
        setRunning(true);
        setMuted(false);
    }
    const goOn = () => {
        const targetMillis = currentPhase.endTime * 1000;
        console.debug("goOn", { targetMillis, currentPhase, upcomingPhases });
        audioScheduler.stop();
        stopwatch.start(targetMillis);
    }

    const currentPhase = upcomingPhases[0];
    const currentAudio = running ? currentPhase?.audio?.[theDiceAreThrown.audioFlavorIndex] || null : null;
    //play / stop audio when currentPhase changes
    useEffect(() => {
        // console.debug("currentAudio changed", { currentAudio });
        if (currentAudio) {
            audioScheduler.play(currentAudio);
        } 
        // return () => {
        //     // console.debug("currentAudio cleanup stop", { currentAudio });
        //     audioScheduler.stop();
        // }
    }, [audioScheduler, currentAudio]);


    const displayClock = true;//currentPhase.type !== "set-intention" && currentPhase.type !== "review-intention";
    const currentPhaseHasAudio = currentPhase?.audioFormats && running;

    const maySkip = currentPhase.maySkipAfter !== undefined && (currentPhase.maySkipAfter === 0 || (currentPhase.elapsedTime !== undefined && currentPhase.elapsedTime >= currentPhase.maySkipAfter));

    // console.debug("TimerDisplay", { maySkip, runningTime: currentPhase.runningTime, maySkipAfter: currentPhase.maySkipAfter });



    useUpdateTitle({ currentPhase, inside: running, roomName: "Timer | The Writing Academic" });

    return (
        <div
            className={"responsive room timer inside" +
                (running ? " running" : "") +
                (isPlayingAudio && currentPhaseHasAudio ? " is-playing-audio" : "")
            }
            id={"room-timer"}
        >

            <ConfirmDialog
                open={showConfirmStopModal}
                title="Willst Du wirklich aufhören?"
                handleClose={() => setShowConfirmStopModal(false)}
                actions={[
                    { label: "Ich mache weiter!", onClick: () => setShowConfirmStopModal(false) },
                    { label: "Wirklich abbrechen", onClick: stopConfirmed },
                ]}>
                Bleib dran - Du hast schon so viel geschafft!
            </ConfirmDialog>

            <div className="room-head" style={{ minHeight: 42.25 }}>
                {running &&
                    (currentPhase.type === "review-intention"

                        ? <Button
                            variant='contained'
                            color={roomColor}
                            size='large'
                            fullWidth={true}
                            onClick={stopConfirmed}
                        >
                            Neu starten
                        </Button>
                        : <Button
                            key="leave-room"
                            variant='outlined'
                            color={roomColor}
                            size='large'
                            fullWidth={true}
                            onClick={stopRequested}
                        >
                            Stop
                        </Button>
                    )
                }
            </div>

            <div className="room-image-container">
                {displayClock
                    ? //display clock
                    <>
                        <div className="layer img"
                            style={{ backgroundImage: `url(${currentPhase.imageInsteadOfClock || conf.graphic.url})` }}
                        >
                        </div>
                        {currentPhaseHasAudio && isPlayingAudio && conf.graphic.urlWithVolume &&
                            <div className="layer img only-show-if-audio-is-playing"
                                style={{ backgroundImage: `url(${conf.graphic.urlWithVolume})` }}
                            >
                            </div>
                        }
                        {!currentPhase.imageInsteadOfClock &&
                            <PhaseRingSegments
                                totalSessionDuration={totalSessionDuration}
                                upcomingPhases={upcomingPhases}
                                graphic={conf.graphic}
                            />
                        }
                    </>
                    : (currentPhase.type === "set-intention"
                        ? //display set intention
                        <div className="layer flex-column">
                            {/* <h3 className="text-center">{currentPhase.name}</h3> */}

                            <TextField
                                fullWidth={true}
                                label="Intention"
                                multiline
                                color={roomColor}
                                rows={5}
                                value={intention}
                                onChange={(event) => setIntention(event.target.value)}
                            />
                        </div>
                        : //display review intention
                        <>

                            {intention
                                ? <div className="layer flex-column">
                                    <h4 className="text-center">Das hattest Du Dir vorgenommen:</h4>
                                    <TextField
                                        fullWidth={true}
                                        label="Intention"
                                        multiline
                                        rows={5}
                                        color={roomColor}
                                        disabled={true}
                                        value={intention}
                                        onChange={(event) => setIntention(event.target.value)}
                                    />
                                    <p className="text-center" style={{ color: roomColorCode }}>Wie lief es? Nimm Dir einen Moment zum Reflektieren!</p>

                                </div>
                                :
                                <div className="layer img pause"
                                    style={{ backgroundImage: `url(${theDiceAreThrown.pauseImage})` }}
                                >
                                </div>
                            }

                        </>
                    )

                }
            </div>

            <div className="room-info">
                {running
                    ? //running 
                    <>
                        <div className="text-center">
                            <p>
                                <span className="text-bold" style={{ color: roomColorCode }}>{currentPhase.name}</span>
                                <span style={{ position: "relative", display: "inline-block", top: -1 }}>
                                    <MuteButton isPlayingAudio={!!(isPlayingAudio)} color={roomColor} />
                                </span>
                                <br />
                                noch <span className="text-bold">{convertSecondsToDurationString(currentPhase.remainingTime ?? currentPhase.duration)}</span>
                            </p>

                            {
                                currentPhase.description &&
                                <p>
                                    {currentPhase.description}
                                </p>
                            }
                        </div>
                        {maySkip &&
                            <Button
                                variant='contained'
                                color={roomColor}
                                size='large'
                                fullWidth={true}
                                onClick={goOn}
                                sx={{ mt: 2 }}
                            >
                                Weiter
                                {currentPhase.remainingTime && currentPhase.remainingTime <= 15 ? " (" + Math.floor(currentPhase.remainingTime) + ")" : ""}
                            </Button>
                        }
                        {(currentPhase.type === "set-intention" || intention) &&
                            <TextField
                                fullWidth={true}
                                label="Intention"
                                multiline
                                maxRows={5}
                                color={roomColor}
                                disabled={currentPhase.type !== "set-intention"}
                                value={intention}
                                onChange={(event) => setIntention(event.target.value)}
                                sx={{ mt: 2 }}
                            />
                        }
                    </>


                    : //not running 
                    <>
                        <div className="text-center" style={{ marginBottom: 20 }}>
                            <p>
                                Wähle die Länge Deiner Fokuszeit:
                            </p>
                            <Slider
                                aria-label="Fokuszeit-Länge"
                                value={focusPhaseLength}
                                getAriaValueText={convertSecondsToDurationString}
                                valueLabelFormat={convertSecondsToDurationString}
                                valueLabelDisplay="auto"
                                marks
                                color={roomColor}
                                step={conf.focusDurationStep}
                                min={conf.minFocusDuration}
                                max={conf.maxFocusDuration}
                                onChange={(event, value) => setFocusPhaseLength(value as number)}
                            />
                        </div>
                        <Button
                            key="start"
                            variant='contained'
                            color={roomColor}
                            size='large'
                            fullWidth={true}
                            onClick={start}
                        >
                            Los geht's!
                        </Button>

                    </>
                }

            </div>

        </div>
    );
};
